import type { FaqAccordionItem } from '@components/FaqAccordion/FaqAccordion';
import { BRIDGE_BIO_CAMPAIGN_CODE } from '@constants/bridgeBio';
import type { CampaignLandingResponse } from '@store/actions';

import { getProductTestDisplayName } from '../../utils/productDisplayName';
import { getProductDescription } from './productDescriptions';
import { ALL_BENEFITS, BenefitItem, getBenefitsByIndexes } from './sections/Benefits/benefitData';
import { FVT_FAQS, getFAQsByIndexes } from './sections/FAQ/faqData';

// Internal config with indexes (used for storage)
interface PartnerConfigRaw {
  benefitIndexes: number[]; // Indexes into ALL_BENEFITS
  campaignCode: string;
  displayName?: string; // Optional formatted display name in Markdown (e.g., "Cancer*EDGE*" for italicized text)
  dynamicBenefits?: BenefitItem[]; // Optional dynamic benefits from API
  faqIndexes: number[]; // Indexes into ALL_FAQS
  headlineDescription?: string;
  headlineTitle?: string; // Optional custom headline title, defaults to standard title if not provided
  logoFileName: string; // Logo file name (e.g., "canceredge.png")
  logoUrl?: string; // Optional S3 logo URL
  name: string;
  urlPath: string; // Exact URL path (e.g., "longevity_canceredge")
  usesPromoCode?: boolean;
}

// Public config with resolved items (used by components)
export interface PartnerConfig {
  allowsMinorWithGuardian?: boolean;
  benefits: BenefitItem[];
  campaignCode: string;
  displayName?: string; // Optional formatted display name in Markdown (e.g., "Cancer*EDGE*" for italicized text)
  dynamicBenefits?: BenefitItem[]; // Optional dynamic benefits from API
  faqs: FaqAccordionItem[];
  headlineDescription?: string;
  headlineTitle?: string;
  logoFileName: string; // Logo file name (e.g., "canceredge.png")
  logoUrl?: string; // Optional S3 logo URL
  name: string;
  urlPath: string; // Exact URL path (e.g., "longevity_canceredge")
  usesPromoCode?: boolean;
}

interface PartnerConfigsRaw {
  [key: string]: PartnerConfigRaw;
}

// Raw configs with indexes - easy to maintain and extend
const partnerConfigsRaw: PartnerConfigsRaw = {
  longevity: {
    name: 'Longevity Life',
    urlPath: 'longevity',
    logoFileName: 'longevity.png',
    campaignCode: 'LONGEVITY',
    // All FAQs (0-9) - using indexes 0-9
    faqIndexes: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], // All FAQs including discrimination-longevity
    // Single-Gene + Medication + all 4 Baseline Risk PRS + Genetic Counseling at end
    benefitIndexes: [0, 1, 3, 4, 5, 6, 2],
  },
  canceredge: {
    name: 'CancerEDGE',
    urlPath: 'longevity_canceredge',
    logoFileName: 'canceredge.png',
    displayName: 'Cancer*EDGE*',
    campaignCode: 'LONGEVITY_CANCEREDGE',
    // First 9 FAQs (0-8) + discrimination FAQ (index 10)
    faqIndexes: [0, 1, 2, 3, 4, 5, 6, 7, 8, 10],
    // CancerEDGE is cancer-focused: Single-Gene + Medication + Breast & Prostate Baseline Risk
    // + Genetic Counseling at end. Intentionally excludes CAD and Type 2 Diabetes Baseline Risk.
    benefitIndexes: [0, 1, 4, 5, 2],
  },
  // Thatch: static config required until BE exposes /patient-portal/partnerships/thatch (fetchCampaignLandingBySlug).
  // When that exists, remove this block so OrderProvider uses API + transformApiToPartnerConfig, and replace
  // transformApiToPartnerConfig's FAQ baseline (today coupled to this entry) with a neutral shared faqIndexes constant.
  thatch: {
    name: 'Thatch',
    urlPath: 'thatch',
    logoFileName: 'thatch.png',
    campaignCode: 'THATCH',
    usesPromoCode: true,
    // First 9 FAQs (0-8) + discrimination FAQ (index 10)
    faqIndexes: [0, 1, 2, 3, 4, 5, 6, 7, 8, 10],
    // All four Baseline Risk PRS products + Access to Genetic Counseling (2)
    benefitIndexes: [0, 1, 3, 4, 5, 6, 2],
  },
  // Add more partners here as needed
  // Example:
  // stellantis: {
  //   name: 'Stellantis',
  //   urlPath: 'stellantis',
  //   logoFileName: 'stellantis.png',
  //   campaignCode: 'STELLANTIS',
  //   headlineTitle: 'Custom Title for Stellantis', // Optional custom title
  //   faqIndexes: [0, 1, 2, 3],
  //   benefitIndexes: [0, 2],
  // },
};

// Default headline title used when partner config doesn't specify one
export const DEFAULT_HEADLINE_TITLE = 'Genomic Insights for Personalized Healthcare';

// Campaigns whose landing page needs bespoke copy or capabilities. BridgeBio FVT comes from the
// campaign landing API rather than partnerConfigsRaw, so its overrides are keyed by campaign code.
const campaignOverrides: Record<string, Partial<PartnerConfig>> = {
  [BRIDGE_BIO_CAMPAIGN_CODE]: {
    allowsMinorWithGuardian: true,
    headlineTitle: 'Family Cascade Genetic Testing for ADH1',
    faqs: FVT_FAQS,
  },
};

/**
 * Helper to format raw config into public config
 */
const formatPartnerConfig = (rawConfig: PartnerConfigRaw): PartnerConfig => {
  return {
    allowsMinorWithGuardian: false,
    campaignCode: rawConfig.campaignCode,
    displayName: rawConfig.displayName,
    headlineDescription: rawConfig.headlineDescription,
    headlineTitle: rawConfig.headlineTitle,
    logoFileName: rawConfig.logoFileName,
    logoUrl: rawConfig.logoUrl,
    name: rawConfig.name,
    urlPath: rawConfig.urlPath,
    usesPromoCode: rawConfig.usesPromoCode,
    faqs: getFAQsByIndexes(rawConfig.faqIndexes),
    benefits: rawConfig.dynamicBenefits || getBenefitsByIndexes(rawConfig.benefitIndexes),
    ...campaignOverrides[rawConfig.campaignCode],
  };
};

/**
 * Transforms an API response into a PartnerConfig
 */
export const transformApiToPartnerConfig = (apiData: CampaignLandingResponse, slug: string): PartnerConfig => {
  // Use Thatch as a baseline for FAQs
  const thatchBaseline = partnerConfigsRaw.thatch;

  // Dynamically map products directly to benefit list items, always append Genetic Counseling at the end
  const dynamicBenefits: BenefitItem[] = [
    ...apiData.products.map(p => {
      const title = getProductTestDisplayName(p);
      return { title, description: getProductDescription(p.report_type, title, p.description) };
    }),
    ALL_BENEFITS[2],
  ];

  const rawConfig: PartnerConfigRaw = {
    name: apiData.name || slug,
    urlPath: slug,
    logoFileName: '', // We use logoUrl instead
    logoUrl: apiData.logo_url,
    campaignCode: apiData.campaign_code,
    // TODO: usesPromoCode could eventually be driven by the API response
    faqIndexes: thatchBaseline.faqIndexes,
    benefitIndexes: [], // Not used when dynamicBenefits is provided
    dynamicBenefits,
  };

  // Campaign-specific copy and capabilities are applied by campaignOverrides in formatPartnerConfig.
  return formatPartnerConfig(rawConfig);
};

/**
 * Check if a partner key is valid
 * @param partnerKey - Partner key to validate
 * @returns True if partner key exists in config
 */
export const isValidPartnerKey = (partnerKey: string): boolean => {
  return partnerKey.toLowerCase() in partnerConfigsRaw;
};

/**
 * Gets partner config by URL path (exact match).
 * @param urlPath The exact URL path from the route (e.g., "longevity_canceredge")
 * @returns PartnerConfig if found, null otherwise
 */
export const getPartnerConfigByUrlPath = (urlPath: string): PartnerConfig | null => {
  if (!urlPath) return null;

  // Find config by matching urlPath field
  const rawConfig = Object.values(partnerConfigsRaw).find(config => config.urlPath === urlPath);
  if (!rawConfig) return null;

  return formatPartnerConfig(rawConfig);
};

/**
 * Gets partner config by campaign code (exact match).
 * @param campaignCode The campaign code (e.g., "LONGEVITY")
 * @returns PartnerConfig if found, null otherwise
 */
export const getPartnerConfigByCampaignCode = (campaignCode: string): PartnerConfig | null => {
  if (!campaignCode) return null;

  // Find config by matching campaignCode field
  const rawConfig = Object.values(partnerConfigsRaw).find(config => config.campaignCode === campaignCode);
  if (!rawConfig) return null;

  return formatPartnerConfig(rawConfig);
};
